import { useCallback } from 'react'
import type { CardFilters } from '@/types'

type SetFilters = React.Dispatch<React.SetStateAction<CardFilters>>
type SetSearchInput = React.Dispatch<React.SetStateAction<string>>

interface UseListFilterActionsArgs {
  setFilters: SetFilters
  setSearchInput: SetSearchInput
  emptyFilters: CardFilters
}

/**
 * Encapsulates the filter/sort/search handlers shared by the list pages
 * (Buylist/Selllist). Pulled out so the page component stays focused on layout
 * and both lists share one copy of the logic.
 *
 * All handlers are `useCallback`-stabilized so consumers can safely pass
 * them to memoized child components without triggering rerenders every tick.
 */
export function useListFilterActions({ setFilters, setSearchInput, emptyFilters }: UseListFilterActionsArgs) {
  const handleFilterChange = useCallback(
    (newFilters: CardFilters) => {
      setFilters(newFilters)
      setSearchInput(newFilters.q || '')
    },
    [setFilters, setSearchInput],
  )

  const handleResetFilters = useCallback(() => {
    setFilters(emptyFilters)
    setSearchInput('')
  }, [setFilters, setSearchInput, emptyFilters])

  const handleSort = useCallback(
    (field: string) => {
      setFilters((prev) => ({
        ...prev,
        sort: field,
        order: prev.sort === field && prev.order === 'asc' ? 'desc' : 'asc',
      }))
    },
    [setFilters],
  )

  const toggleOrder = useCallback(() => {
    setFilters((prev) => ({ ...prev, order: prev.order === 'desc' ? 'asc' : 'desc' }))
  }, [setFilters])

  return { handleFilterChange, handleResetFilters, handleSort, toggleOrder }
}
